import React, { useState } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AppFooter from '@core/components/AppFooter';
import AuthBackToHome from '@features/auth/components/AuthBackToHome';
import { ErrorResponse } from '@core/types/errorResponse';
import {
  formFieldErrorsFromApiErrors,
  hasFormFieldErrorMessages,
} from '@core/utils/apiErrorFieldValue';
import { checkUserEligibility } from '@features/auth/api/authApi';
import {
  EMAIL_VERIFICATION_CODE_BACK,
  EMAIL_VERIFICATION_CODE_FORM_ARIA_LABEL,
  EMAIL_VERIFICATION_CODE_HELP,
  EMAIL_VERIFICATION_CODE_LABEL,
  EMAIL_VERIFICATION_CODE_PLACEHOLDER,
  EMAIL_VERIFICATION_CODE_SENT_PREFIX,
  EMAIL_VERIFICATION_CODE_SUBMIT,
  EMAIL_VERIFICATION_CODE_SUBMIT_LOADING,
  EMAIL_VERIFICATION_EMAIL_FORM_ARIA_LABEL,
  EMAIL_VERIFICATION_EMAIL_HELP,
  EMAIL_VERIFICATION_EMAIL_LABEL,
  EMAIL_VERIFICATION_EMAIL_PATTERN,
  EMAIL_VERIFICATION_EMAIL_PLACEHOLDER,
  EMAIL_VERIFICATION_EMAIL_SUBMIT,
  EMAIL_VERIFICATION_EMAIL_SUBMIT_LOADING,
  EMAIL_VERIFICATION_ERROR_CODE_REQUIRED,
  EMAIL_VERIFICATION_ERROR_EMAIL_INVALID,
  EMAIL_VERIFICATION_ERROR_EMAIL_REQUIRED,
  EMAIL_VERIFICATION_ERROR_INVALID_INVITATION,
  EMAIL_VERIFICATION_ERROR_REQUEST_FAILED,
  EMAIL_VERIFICATION_ERROR_UNEXPECTED_RESPONSE,
  EMAIL_VERIFICATION_ERROR_VERIFY_FAILED,
  EMAIL_VERIFICATION_LOGIN_LINK,
  EMAIL_VERIFICATION_LOGO_ALT,
  EMAIL_VERIFICATION_PAGE_HEADING,
  EMAIL_VERIFICATION_REQUIRED_MARKER,
  EMAIL_VERIFICATION_STEP_SUBTITLE,
  EMAIL_VERIFICATION_SUCCESS_CODE_SENT,
  EMAIL_VERIFICATION_SUCCESS_LOGIN_REDIRECT,
  LOGIN_REDIRECT_DELAY_MS,
} from '@features/auth/constants/emailVerificationConstants';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { showSuccess } from '@infrastructure/api/utils/toastNotifications';
import '../login/login.css';
import loginImage from '../login/ntcog-logo.png';

interface EmailVerificationProps {
  onVerificationSuccess: (email: string, verificationCode: string, invitationCode?: string) => void;
}

interface EmailForm {
  email: string;
}

type FieldErrors = Partial<Record<keyof EmailForm | 'verification_code', string[]>>;

const EmailVerification: React.FC<EmailVerificationProps> = ({ onVerificationSuccess }) => {
  const [form, setForm] = useState<EmailForm>({ email: '' });
  const [verificationCode, setVerificationCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<FieldErrors>({});
  const showError = useApiError();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'email') {
      setForm(prev => ({ ...prev, [name]: value }));
    } else if (name === 'verification_code') {
      setVerificationCode(value);
    }

    const fieldName = name as keyof FieldErrors;
    if (formErrors[fieldName]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }
  };

  const validateEmail = (): boolean => {
    const errors: FieldErrors = {};
    
    if (!form.email.trim()) {
      errors.email = [EMAIL_VERIFICATION_ERROR_EMAIL_REQUIRED];
    } else if (!EMAIL_VERIFICATION_EMAIL_PATTERN.test(form.email)) {
      errors.email = [EMAIL_VERIFICATION_ERROR_EMAIL_INVALID];
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCode = (): boolean => {
    const errors: FieldErrors = {};
    
    if (!verificationCode.trim()) {
      errors.verification_code = [EMAIL_VERIFICATION_ERROR_CODE_REQUIRED];
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    
    if (!validateEmail()) {
      return;
    }
    
    setLoading(true);

    try {
      const response = await checkUserEligibility(form.email);
      
      if (response.isSuccess()) {
        const data = response.getData();
        
        if (data?.next_steps === 'SIGNUPREQUIRED') {
          setShowCodeInput(true);
          setFormErrors({});
          showSuccess(EMAIL_VERIFICATION_SUCCESS_CODE_SENT);
        } else if (data?.next_steps === 'LOGINREQUIRED') {
          showSuccess(EMAIL_VERIFICATION_SUCCESS_LOGIN_REDIRECT);
          setTimeout(() => {
            navigate('/login');
          }, LOGIN_REDIRECT_DELAY_MS);
        } else {
          setFormErrors({
            email: [EMAIL_VERIFICATION_ERROR_UNEXPECTED_RESPONSE]
          });
        }
      } else {
        const error = response.getError();
        if (!error) {
          setFormErrors({ email: [EMAIL_VERIFICATION_ERROR_VERIFY_FAILED] });
        } else if (response.status === 401) {
          setFormErrors({ email: [EMAIL_VERIFICATION_ERROR_INVALID_INVITATION] });
        } else {
          const apiFieldErrors = formFieldErrorsFromApiErrors(error.errors);
          setFormErrors(
            hasFormFieldErrorMessages(apiFieldErrors)
              ? apiFieldErrors
              : { email: [EMAIL_VERIFICATION_ERROR_VERIFY_FAILED] }
          );
        }
      }
    } catch (error) {
      setFormErrors({
        email: [EMAIL_VERIFICATION_ERROR_REQUEST_FAILED]
      });
      showError(error as ErrorResponse);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    
    if (!validateCode()) {
      return;
    }
    
    onVerificationSuccess(form.email, verificationCode);
  };

  return (
    <div className="login-wrapper min-vh-100 d-flex flex-column">
      <AuthBackToHome />
      <main
        id="main"
        className="flex-grow-1 d-flex align-items-center justify-content-center py-4"
        tabIndex={-1}
        data-testid="register-email-step-page"
      >
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} xl={6}>
              <Card className="login-card shadow-lg border-0" data-testid="register-email-step-card">
                <Row className="g-0">
                  <Col
                    md={6}
                    className="d-none d-md-flex align-items-center justify-content-center login-image bg-light"
                    data-testid="register-email-step-illustration-panel"
                  >
                    <img src={loginImage} alt={EMAIL_VERIFICATION_LOGO_ALT} className="img-fluid" />
                  </Col>

                  <Col md={6} className="d-flex align-items-center">
                    <Card.Body className="p-4 p-md-5 w-100 auth-card__body">
                      <h1 className="mb-4 text-center fw-bold text-dark auth-page-heading">{EMAIL_VERIFICATION_PAGE_HEADING}</h1>
                      <p className="text-center text-muted mb-4">
                        {EMAIL_VERIFICATION_STEP_SUBTITLE}
                      </p>

                      {!showCodeInput ? (
                        <Form
                          noValidate
                          onSubmit={handleSubmitEmail}
                          className="login-form"
                          aria-label={EMAIL_VERIFICATION_EMAIL_FORM_ARIA_LABEL}
                          data-testid="register-email-form"
                        >
                          <Form.Group className="mb-4" controlId="email">
                            <Form.Label>
                              {EMAIL_VERIFICATION_EMAIL_LABEL}{' '}
                              <span className="text-danger">{EMAIL_VERIFICATION_REQUIRED_MARKER}</span>
                            </Form.Label>
                            <Form.Control
                              type="email"
                              name="email"
                              placeholder={EMAIL_VERIFICATION_EMAIL_PLACEHOLDER}
                              value={form.email}
                              onChange={handleChange}
                              autoComplete="email"
                              isInvalid={!!formErrors.email}
                              data-testid="register-email-input"
                            />
                            <Form.Control.Feedback type="invalid" role="alert">
                              {formErrors.email?.[0]}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                              {EMAIL_VERIFICATION_EMAIL_HELP}
                            </Form.Text>
                          </Form.Group>

                          <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="w-100 mb-3"
                            disabled={loading}
                            aria-busy={loading}
                            data-testid="register-email-submit-button"
                          >
                            {loading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                />
                                {EMAIL_VERIFICATION_EMAIL_SUBMIT_LOADING}
                              </>
                            ) : (
                              EMAIL_VERIFICATION_EMAIL_SUBMIT
                            )}
                          </Button>
                        </Form>
                      ) : (
                        <Form
                          noValidate
                          onSubmit={handleSubmitCode}
                          className="login-form"
                          aria-label={EMAIL_VERIFICATION_CODE_FORM_ARIA_LABEL}
                          data-testid="register-code-form"
                        >
                          <div className="alert alert-success mb-4" data-testid="register-code-success-alert">
                            {EMAIL_VERIFICATION_CODE_SENT_PREFIX} {form.email}
                          </div>

                          <Form.Group className="mb-4" controlId="verification_code">
                            <Form.Label>
                              {EMAIL_VERIFICATION_CODE_LABEL}{' '}
                              <span className="text-danger">{EMAIL_VERIFICATION_REQUIRED_MARKER}</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="verification_code"
                              placeholder={EMAIL_VERIFICATION_CODE_PLACEHOLDER}
                              value={verificationCode}
                              onChange={handleChange}
                              isInvalid={!!formErrors.verification_code}
                              autoFocus
                              data-testid="register-code-input"
                            />
                            <Form.Control.Feedback type="invalid" role="alert">
                              {formErrors.verification_code?.[0]}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                              {EMAIL_VERIFICATION_CODE_HELP}
                            </Form.Text>
                          </Form.Group>

                          <div className="d-flex flex-column flex-sm-row gap-2 mb-3">
                            <Button
                              variant="outline-secondary"
                              size="lg"
                              className="w-100 flex-sm-fill"
                              onClick={() => {
                                setShowCodeInput(false);
                                setVerificationCode('');
                                setFormErrors({});
                              }}
                              disabled={loading}
                              data-testid="register-code-back-button"
                            >
                              {EMAIL_VERIFICATION_CODE_BACK}
                            </Button>
                            <Button
                              type="submit"
                              variant="primary"
                              size="lg"
                              className="w-100 flex-sm-fill"
                              disabled={loading}
                              aria-busy={loading}
                              data-testid="register-code-submit-button"
                            >
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  {EMAIL_VERIFICATION_CODE_SUBMIT_LOADING}
                                </>
                              ) : (
                                EMAIL_VERIFICATION_CODE_SUBMIT
                              )}
                            </Button>
                          </div>
                        </Form>
                      )}

                      <div className="auth-secondary-link-wrap">
                        <Button
                          variant="link"
                          className="text-muted p-0 forgot-password auth-secondary-link"
                          onClick={() => navigate('/login')}
                          data-testid="register-login-link"
                        >
                          {EMAIL_VERIFICATION_LOGIN_LINK}
                        </Button>
                      </div>
                    </Card.Body>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Container>
      </main>
      <AppFooter />
    </div>
  );
};

export default EmailVerification;
